import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { ClientQualityStateService } from './client-quality-state.service';
import {
  EarnAttributeStatus,
  EarnDimension,
  EarnEvent,
  NpsCategory,
  NpsEvent,
  QualityState,
  QualityTimeWindow,
} from './client-quality.model';

const TIME_WINDOW_OPTIONS: { value: QualityTimeWindow; label: string }[] = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 180 days' },
  { value: '1y', label: 'Last year' },
  { value: 'all', label: 'All time' },
];

@Component({
  selector: 'bai-client-quality',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TitleCasePipe, TagModule, SkeletonModule, TooltipModule],
  templateUrl: './client-quality.component.html',
  styleUrl: './client-quality.component.scss',
})
export class ClientQualityComponent {
  private readonly _session = inject(ClientSessionService);
  private readonly _stateService = inject(ClientQualityStateService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);

  readonly loading = signal(true);
  readonly timeWindowOptions = TIME_WINDOW_OPTIONS;

  /** Expanded verbatim ids. */
  readonly expandedVerbatim = signal<Set<string>>(new Set());

  private readonly _sessionId = computed(
    () => this._session.selectedSession()?.uiSessionId ?? null
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        this._stateService.ensureState(id);
        this.loading.set(false);
      } else {
        this.loading.set(false);
      }
    });
  }

  readonly state = computed((): QualityState | null => {
    const id = this._sessionId();
    if (!id) return null;
    const snap = this._stateService.storeSnapshot()[id];
    if (!snap) return null;
    return this._stateService.getState(id);
  });

  // ---------------------------------------------------------------------------
  // Time window
  // ---------------------------------------------------------------------------

  setTimeWindow(window: QualityTimeWindow): void {
    const id = this._sessionId();
    if (id) this._stateService.setTimeWindow(id, window);
  }

  // ---------------------------------------------------------------------------
  // NPS helpers
  // ---------------------------------------------------------------------------

  npsScoreClass(category: NpsCategory): string {
    if (category === 'promoter') return 'bai-quality__nps-score--promoter';
    if (category === 'passive')  return 'bai-quality__nps-score--passive';
    return 'bai-quality__nps-score--detractor';
  }

  npsCategoryLabel(category: NpsCategory): string {
    if (category === 'promoter') return 'Promoter';
    if (category === 'passive')  return 'Passive';
    return 'Detractor';
  }

  npsCategorySeverity(category: NpsCategory): 'success' | 'warn' | 'danger' {
    if (category === 'promoter') return 'success';
    if (category === 'passive')  return 'warn';
    return 'danger';
  }

  npsChannelIcon(channel: string): string {
    const map: Record<string, string> = {
      email: 'pi pi-envelope',
      chat: 'pi pi-comments',
      call_transfer: 'pi pi-phone',
      manual: 'pi pi-pencil',
    };
    return map[channel] ?? 'pi pi-question';
  }

  npsChannelLabel(channel: string): string {
    const map: Record<string, string> = {
      email: 'Email',
      chat: 'Chat',
      call_transfer: 'Call Transfer',
      manual: 'Manual',
    };
    return map[channel] ?? channel;
  }

  // ---------------------------------------------------------------------------
  // EARN helpers
  // ---------------------------------------------------------------------------

  earnScoreClass(score: number): string {
    if (score >= 80) return 'bai-quality__earn-score--strong';
    if (score >= 60) return 'bai-quality__earn-score--caution';
    return 'bai-quality__earn-score--risk';
  }

  earnScoreLabel(score: number): string {
    if (score >= 80) return 'Strong';
    if (score >= 60) return 'Caution';
    return 'At Risk';
  }

  earnScoreSeverity(score: number): 'success' | 'warn' | 'danger' {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warn';
    return 'danger';
  }

  attrStatusClass(status: EarnAttributeStatus): string {
    const map: Record<EarnAttributeStatus, string> = {
      strong:  'bai-quality__attr--strong',
      caution: 'bai-quality__attr--caution',
      risk:    'bai-quality__attr--risk',
      unknown: 'bai-quality__attr--unknown',
    };
    return map[status];
  }

  attrStatusIcon(status: EarnAttributeStatus): string {
    const map: Record<EarnAttributeStatus, string> = {
      strong:  'pi pi-check-circle',
      caution: 'pi pi-exclamation-circle',
      risk:    'pi pi-times-circle',
      unknown: 'pi pi-minus-circle',
    };
    return map[status];
  }

  dimScoreClass(score: number): string {
    if (score >= 80) return 'bai-quality__dim-score--strong';
    if (score >= 60) return 'bai-quality__dim-score--caution';
    return 'bai-quality__dim-score--risk';
  }

  // ---------------------------------------------------------------------------
  // Timeline helpers
  // ---------------------------------------------------------------------------

  formatTimestamp(iso: string): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso));
  }

  sourceLabel(source: string): string {
    const map: Record<string, string> = {
      fa: 'FA', ai: 'AI Agent', system: 'System',
    };
    return map[source] ?? source;
  }

  toggleVerbatim(id: string): void {
    this.expandedVerbatim.update(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  isVerbatimExpanded(id: string): boolean {
    return this.expandedVerbatim().has(id);
  }

  isNpsEvent(event: { type: string }): event is NpsEvent {
    return event.type === 'nps';
  }

  isEarnEvent(event: { type: string }): event is EarnEvent {
    return event.type === 'earn';
  }

  addNpsEventToCopilot(event: NpsEvent): void {
    this._copilotComposer.addContext({
      sourceType: 'quality-nps',
      sourceId: event.id,
      label: `NPS: ${event.score}/10`,
      fullText:
        `[Quality | NPS | ${event.id}] ` +
        `Score: ${event.score}/10; ` +
        `Category: ${this.npsCategoryLabel(event.category)}; ` +
        `Channel: ${this.npsChannelLabel(event.channel)}; ` +
        `Case Status: ${event.caseStatus}; ` +
        `Timestamp: ${event.timestamp}; ` +
        `AI Summary: ${event.aiSummaryBullets.length > 0 ? event.aiSummaryBullets.join(' | ') : '—'}; ` +
        `Verbatim: ${event.verbatim ?? '—'}`,
    });
  }

  addEarnEventToCopilot(event: EarnEvent): void {
    const dimensionSummary = event.dimensions
      .map((dimension) => `${dimension.label} ${dimension.score}`)
      .join(', ');

    this._copilotComposer.addContext({
      sourceType: 'quality-earn',
      sourceId: event.id,
      label: `EARN: ${event.score0to100}/100`,
      fullText:
        `[Quality | EARN | ${event.id}] ` +
        `Score: ${event.score0to100}/100; ` +
        `Assessed Entity: ${event.assessedEntityType.toUpperCase()} ${event.assessedEntityName}; ` +
        `Case Status: ${event.caseStatus}; ` +
        `Timestamp: ${event.timestamp}; ` +
        `Dimensions: ${dimensionSummary}; ` +
        `AI Summary: ${event.aiSummaryBullets.length > 0 ? event.aiSummaryBullets.join(' | ') : '—'}; ` +
        `Recommendations: ${event.recommendations.length > 0 ? event.recommendations.join(' | ') : '—'}; ` +
        `Verbatim: ${event.verbatim ?? '—'}`,
    });
  }

  addTimelineEventToCopilot(event: NpsEvent | EarnEvent): void {
    if (event.type === 'nps') {
      this.addNpsEventToCopilot(event);
      return;
    }
    this.addEarnEventToCopilot(event);
  }

  // ---------------------------------------------------------------------------
  // Track helpers
  // ---------------------------------------------------------------------------

  trackById(_: number, item: { id: string }): string { return item.id; }
  trackByDimId(_: number, dim: EarnDimension): string { return dim.id; }
}
